import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Snackbar,
  Alert,
} from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import Slider from "react-slick";

/** Default mark on built-in carousel slides (MSI³ roundel). */
import msi3CarouselLogo from "assets/images/logos/msi3-logo-carousel.png";
import { AuthContext } from "@healthdesk/shared-hooks";
import useMapCarouselGlobalConfig from "../../hooks/useMapCarouselGlobalConfig";
import usePublicMapCarouselStats from "../../hooks/usePublicMapCarouselStats";
import {
  computeFinalCarouselSlides,
  createButtonClickForHref,
  createCarouselGlobalButtonClick,
  duplicateSingleSlideForSlick,
  emitMapCarouselEvent,
  getAllEnabledGlobalSlideRows,
  getEffectiveGlobalSlideRows,
  getMapCarouselViewerSegment,
  LISTING_TYPE_LOCAL_CAROUSEL,
  localCarouselAdVisibleOnMap,
  MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX,
  MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX,
  normalizeMergeSettings,
  orderGlobalRowsForMapCarousel,
  recordCarouselClickAndStashForInternalPath,
  sortPaidCarouselAdsByCpcDesc,
  debitMapCarouselCpcClickIfNeeded,
  debitMapCarouselCpmImpressionIfNeeded,
} from "../../services/mapCarousel";
import {
  MAP_CAROUSEL_MAP_BOTTOM_INSET_PX,
  MAP_HEADER_FLOAT_ROW_TOP_PX,
  MAP_HEADER_INLINE_RIGHT_RESERVE_PX,
} from "./mapHeaderFloatLayout";

/** Primary ad line — well above default body2 (~0.875rem); matches legacy carousel presence. */
const CAROUSEL_MESSAGE_TYPOGRAPHY = {
  fontSize: { xs: "1.0625rem", sm: "1.125rem", md: "1.25rem" },
  lineHeight: 1.35,
};

/** Built-in MSI³ roundel — max diameter while ~matching pre-change strip height (tighter py/mb/button). */
const CAROUSEL_MSI3_LOGO_PX = 64;

/** Same geometry as the original bottom-docked carousel: arrow sits to the left of the slide strip. */
const CarouselPrevArrow = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    size="small"
    aria-label="Previous slide"
    sx={{
      position: "absolute",
      top: "50%",
      left: -36,
      transform: "translateY(-50%)",
      zIndex: 2,
      backgroundColor: "background.paper",
      boxShadow: 2,
      width: 32,
      height: 32,
      "&:hover": {
        backgroundColor: "background.paper",
        boxShadow: 4,
      },
    }}
  >
    <ChevronLeft color="primary" fontSize="small" />
  </IconButton>
);

/** Slick requires a nextArrow node; hide the forward “>” control. */
function HiddenSlickNextArrow({ className, style }) {
  return (
    <span
      className={className}
      style={{ ...style, display: "none", width: 0, height: 0, overflow: "hidden" }}
      aria-hidden
    />
  );
}

const BILLING_ERROR_SNACKBAR_MS = 8_000;

const YOUTUBE_EMBED_MIN_HEIGHT_PX = 270;

const AdSlide = ({
  image,
  message,
  buttonClick,
  buttonText,
  coordinates,
  facilityType,
  setFilter,
  website,
  isPaidAd,
  isBuiltIn,
  title,
  onBeforeOpenPaidWebsite,
  mapCarouselLinkToVideo,
  mapCarouselYoutubeVideoId,
  mapCarouselLearnMoreHref,
  onWatchVideo,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const handleClick = () => {
    if (facilityType && setFilter) {
      setFilter((prev) => ({
        ...prev,
        facility: facilityType,
        ...(facilityType === "emergency" && { rating: 1 }),
        ...(facilityType !== "emergency" && { rating: "" }),
      }));
    }

    if (buttonClick) {
      buttonClick();
    }
  };

  const handleButtonClick = async (e) => {
    e.stopPropagation();
    if (isBuiltIn) {
      handleClick();
      return;
    }
    if (mapCarouselLinkToVideo && mapCarouselYoutubeVideoId) {
      onWatchVideo?.();
      return;
    }
    if (!website) {
      return;
    }
    try {
      await onBeforeOpenPaidWebsite?.();
    } catch (err) {
      console.warn("AdSlide: paid open blocked", err);
      return;
    }
    window.open(website, "_blank");
  };

  const handleLearnMoreClick = (e) => {
    e.stopPropagation();
    const u = typeof mapCarouselLearnMoreHref === "string" ? mapCarouselLearnMoreHref.trim() : "";
    if (!u) {
      return;
    }
    window.open(u, "_blank");
  };

  return (
    <Paper
      elevation={2}
      onClick={handleClick}
      sx={{
        display: "flex !important",
        flexDirection: { xs: "column", lg: "row" },
        alignItems: { xs: "flex-start", lg: "center" },
        cursor: "pointer",
        px: 1.25,
        py: 0.5,
        borderRadius: 2,
        mb: 0,
        width: "100%",
        textAlign: "left",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: { xs: "100%", lg: "auto" },
          mb: { xs: isPaidAd ? 0.5 : 0, lg: 0 },
        }}
      >
        <Box
          component="img"
          src={image ? image : msi3CarouselLogo}
          alt=""
          sx={{
            ...(image
              ? {
                  maxWidth: `${MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX}px`,
                  maxHeight: `${MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX}px`,
                  width: "auto",
                  height: "auto",
                }
              : {
                  width: CAROUSEL_MSI3_LOGO_PX,
                  height: CAROUSEL_MSI3_LOGO_PX,
                  maxWidth: CAROUSEL_MSI3_LOGO_PX,
                  maxHeight: CAROUSEL_MSI3_LOGO_PX,
                }),
            mr: 1.5,
            flexShrink: 0,
            objectFit: "contain",
          }}
        />

        <Typography
          variant="caption"
          sx={{
            color: "grey.600",
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            flexShrink: 0,
            fontWeight: 500,
          }}
        >
          {isPaidAd ? "Sponsored" : "Ad"}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          my: { xs: 0.75, lg: 0 },
          mx: { xs: 0, lg: 2 },
          order: { xs: 2, lg: 1 },
        }}
      >
        <Typography
          variant="body1"
          component="div"
          sx={{
            color: theme.palette.primary.main,
            whiteSpace: "pre-line",
            textAlign: "left",
            fontWeight: 500,
            ...CAROUSEL_MESSAGE_TYPOGRAPHY,
            "& strong": { fontWeight: 700 },
          }}
          {...(isBuiltIn
            ? { dangerouslySetInnerHTML: { __html: message } }
            : { children: message })}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: { xs: "flex-start", lg: "flex-end" },
          alignItems: "center",
          width: { xs: "100%", lg: "auto" },
          mt: { xs: 0.5, lg: 0 },
          order: { xs: 3, lg: 2 },
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          size={isDesktop ? "medium" : "small"}
          onClick={handleButtonClick}
          sx={{
            fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
            fontWeight: 600,
            px: { xs: 1.75, md: 2 },
            py: { xs: 0.5, md: 0.5 },
            minHeight: { xs: 32, md: 34 },
            textTransform: "none",
            flexShrink: 0,
          }}
        >
          {isBuiltIn
            ? buttonText
            : mapCarouselLinkToVideo
              ? "Watch video"
              : "Visit Site"}
        </Button>
        {!isBuiltIn &&
        isPaidAd &&
        mapCarouselLinkToVideo &&
        typeof mapCarouselLearnMoreHref === "string" &&
        mapCarouselLearnMoreHref.trim() ? (
          <Button
            variant="text"
            size={isDesktop ? "medium" : "small"}
            onClick={handleLearnMoreClick}
            sx={{
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Learn more
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
};

/** @typedef {"mapHeader" | "appBarRow" | "mapBottom"} AdCarouselPlacement */

const AdCarousel = ({
  ads,
  onLocationClick,
  setFilter,
  listingsLoaded,
  /** Left inset (px) from header edge; small gutter for alignment with map chrome. */
  inlineLeftOffsetPx = 8,
  /**
   * `mapHeader`: absolutely positioned below CareMap header (default).
   * `appBarRow`: in-flow strip for dashboard AppBar (e.g. professional-chartmind).
   * `mapBottom`: docked near the bottom of the map pane (stacked embed, e.g. /scmap).
   */
  placement = "mapHeader",
}) => {
  const [slides, setSlides] = useState([]);
  const [billingMessage, setBillingMessage] = useState(null);
  const [youtubeModalVideoId, setYoutubeModalVideoId] = useState(null);
  const skipDuplicateCpmForAfterChangeZeroRef = useRef(false);
  /** Latest slides for analytics effects that intentionally omit `slides` from dependency arrays. */
  const slidesRef = useRef(slides);
  slidesRef.current = slides;
  const { config: mapCarouselConfig } = useMapCarouselGlobalConfig();
  const { user, userData } = useContext(AuthContext);
  const { statsBySlideId, statsReady } = usePublicMapCarouselStats();

  useEffect(() => {
    if (!listingsLoaded) {
      return;
    }

    const paidAds =
      ads?.filter((ad) => {
        if (ad.type === "promotion" || ad.type === "featured") {
          return true;
        }
        if (ad.type === LISTING_TYPE_LOCAL_CAROUSEL) {
          return localCarouselAdVisibleOnMap(ad, userData?.role, userData);
        }
        return false;
      }) || [];

    const paidSlides = sortPaidCarouselAdsByCpcDesc(
      paidAds.map((ad) => {
        const localCarouselSlideId =
          ad.type === LISTING_TYPE_LOCAL_CAROUSEL &&
          typeof ad.localCarouselSlideId === "string" &&
          ad.localCarouselSlideId
            ? ad.localCarouselSlideId
            : undefined;
        return {
          logo: ad.logo,
          message: ad.tagline,
          button: ad.localCarouselButtonLabel || "Visit Site",
          buttonClick: () => {
            if (ad.coordinates && onLocationClick) {
              onLocationClick(ad.coordinates);
            }
          },
          coordinates: ad.coordinates,
          facilityType: ad.facilityType,
          website: ad.website,
          title: ad.title || ad.group,
          isPaidAd: true,
          isBuiltIn: false,
          carouselSlideId: localCarouselSlideId,
          sortCpc: Number(ad.sortCpc) || 0,
          type: ad.type,
          mapCarouselListingDocId: ad.mapCarouselListingDocId,
          mapCarouselSourceZip: ad.mapCarouselSourceZip,
          mapCarouselCpcChargeCents: Number(ad.mapCarouselCpcChargeCents) || 0,
          mapCarouselLinkToVideo: Boolean(ad.mapCarouselLinkToVideo),
          mapCarouselYoutubeVideoId: ad.mapCarouselYoutubeVideoId || null,
          mapCarouselLearnMoreHref: ad.mapCarouselLearnMoreHref || "",
          mapCarouselCpmChargeCentsPerImpression:
            Number(ad.mapCarouselCpmChargeCentsPerImpression) || 0,
        };
      })
    );

    const viewerSegment = getMapCarouselViewerSegment(userData?.role);
    const segmentRows = getEffectiveGlobalSlideRows(mapCarouselConfig, viewerSegment);
    const allEnabledRows = getAllEnabledGlobalSlideRows(mapCarouselConfig);
    const anonymous = !user;
    const globalRows = orderGlobalRowsForMapCarousel(segmentRows, allEnabledRows, {
      anonymous,
      statsBySlideId,
      statsReady,
    });
    const globalSlides = globalRows.map((row) => {
      const sid = typeof row.id === "string" && row.id.trim() ? row.id.trim() : "";
      return {
        message: row.message,
        button: row.buttonLabel,
        buttonClick: sid
          ? createCarouselGlobalButtonClick(row.href, sid)
          : createButtonClickForHref(row.href),
        logo: row.logoUrl || undefined,
        isPaidAd: false,
        isBuiltIn: true,
        carouselSlideId: sid || undefined,
      };
    });

    const { mergeMode, minPaidAds } = normalizeMergeSettings(mapCarouselConfig);

    let finalSlides = computeFinalCarouselSlides({
      globalSlides,
      paidSlides,
      mergeMode,
      minPaidAds,
    });

    finalSlides = duplicateSingleSlideForSlick(finalSlides);
    setSlides(finalSlides);
  }, [
    ads,
    onLocationClick,
    listingsLoaded,
    mapCarouselConfig,
    user,
    userData,
    statsBySlideId,
    statsReady,
  ]);

  const carouselSlickKey = useMemo(
    () =>
      slides
        .map((s) => s.carouselSlideId || (s.isPaidAd ? "paid" : "builtin"))
        .join("|"),
    [slides]
  );

  const debitCpmForSlideIfNeeded = useCallback(async (slide) => {
    if (!slide || slide.__slickDup) {
      return;
    }
    if (
      slide.type !== LISTING_TYPE_LOCAL_CAROUSEL ||
      !slide.carouselSlideId ||
      !slide.mapCarouselLinkToVideo ||
      (Number(slide.mapCarouselCpmChargeCentsPerImpression) || 0) <= 0
    ) {
      return;
    }
    const d = await debitMapCarouselCpmImpressionIfNeeded({
      zip: slide.mapCarouselSourceZip,
      listingDocId: slide.mapCarouselListingDocId,
      slideId: slide.carouselSlideId,
      chargeCents: slide.mapCarouselCpmChargeCentsPerImpression,
    });
    if (!d.ok) {
      setBillingMessage(
        d.message || "Could not record ad impression billing for this video slide."
      );
    }
  }, []);

  useEffect(() => {
    skipDuplicateCpmForAfterChangeZeroRef.current = false;
  }, [carouselSlickKey]);

  useEffect(() => {
    if (!listingsLoaded) {
      return;
    }
    const currentSlides = slidesRef.current;
    if (currentSlides.length === 0) {
      return;
    }
    const first = currentSlides[0];
    if (!first?.carouselSlideId) {
      return;
    }
    void emitMapCarouselEvent({
      slideId: first.carouselSlideId,
      kind: "impression",
    });
    void debitCpmForSlideIfNeeded(first);
    skipDuplicateCpmForAfterChangeZeroRef.current = true;
    // Omit `slides` from deps: new array on every stats snapshot would re-emit impressions in a loop.
  }, [listingsLoaded, carouselSlickKey, debitCpmForSlideIfNeeded]);

  const slickSettings = useMemo(
    () => ({
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      speed: 500,
      dots: false,
      autoplay: true,
      autoplaySpeed: 7000,
      prevArrow: <CarouselPrevArrow />,
      nextArrow: <HiddenSlickNextArrow />,
      afterChange: (index) => {
        const slide = slides[index];
        if (slide?.carouselSlideId) {
          void emitMapCarouselEvent({
            slideId: slide.carouselSlideId,
            kind: "impression",
          });
        }
        if (index === 0 && skipDuplicateCpmForAfterChangeZeroRef.current) {
          skipDuplicateCpmForAfterChangeZeroRef.current = false;
        } else {
          void debitCpmForSlideIfNeeded(slide);
        }
      },
    }),
    [slides, debitCpmForSlideIfNeeded]
  );

  if (!listingsLoaded || slides.length === 0) {
    return null;
  }

  const CAROUSEL_ARROW_GUTTER_PX = 44;

  const outerSx =
    placement === "appBarRow"
      ? {
          position: "relative",
          width: "100%",
          left: "auto",
          top: "auto",
          right: "auto",
          zIndex: 2,
          maxWidth: "none",
          pointerEvents: "auto",
          pl: `${CAROUSEL_ARROW_GUTTER_PX + inlineLeftOffsetPx}px`,
          pr: 2,
          py: 0.5,
          boxSizing: "border-box",
          overflow: "visible",
        }
      : placement === "mapBottom"
        ? {
            position: "absolute",
            bottom: `${MAP_CAROUSEL_MAP_BOTTOM_INSET_PX}px`,
            left: `${inlineLeftOffsetPx}px`,
            right: `${inlineLeftOffsetPx}px`,
            zIndex: 10001,
            maxWidth: "none",
            pointerEvents: "auto",
            paddingLeft: `${CAROUSEL_ARROW_GUTTER_PX}px`,
            boxSizing: "border-box",
            overflow: "visible",
          }
        : {
            position: "absolute",
            top: `calc(100% + ${MAP_HEADER_FLOAT_ROW_TOP_PX}px)`,
            left: `${inlineLeftOffsetPx}px`,
            right: `${MAP_HEADER_INLINE_RIGHT_RESERVE_PX}px`,
            zIndex: 10001,
            maxWidth: "none",
            pointerEvents: "auto",
            paddingLeft: `${CAROUSEL_ARROW_GUTTER_PX}px`,
            boxSizing: "border-box",
          };

  const innerSliderSx = {
    position: "relative",
    maxWidth: 860,
    width: "100%",
    minWidth: 0,
    ...((placement === "appBarRow" || placement === "mapBottom") && { mx: "auto" }),
    textAlign: "left",
    "& .slick-list": {
      textAlign: "left",
    },
    "& .slick-track": {
      display: "flex",
      alignItems: "center",
    },
    "& .slick-slide": {
      padding: "0 4px",
      textAlign: "left",
      height: "auto",
    },
    "& .slick-slide > div": {
      textAlign: "left",
      width: "100%",
    },
  };

  return (
    <>
      <Dialog
        open={Boolean(youtubeModalVideoId)}
        onClose={() => setYoutubeModalVideoId(null)}
        maxWidth="md"
        fullWidth
        aria-labelledby="carousel-youtube-dialog-title"
      >
        <DialogContent sx={{ p: 1.5, pt: 2 }}>
          <Typography id="carousel-youtube-dialog-title" variant="subtitle2" sx={{ mb: 1 }}>
            Sponsored video
          </Typography>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              minWidth: { xs: "100%", sm: 480 },
              minHeight: YOUTUBE_EMBED_MIN_HEIGHT_PX,
              paddingTop: "56.25%",
              bgcolor: "common.black",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {youtubeModalVideoId ? (
              <iframe
                title="Sponsored YouTube video"
                src={`https://www.youtube.com/embed/${youtubeModalVideoId}?rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
              />
            ) : null}
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(billingMessage)}
        autoHideDuration={BILLING_ERROR_SNACKBAR_MS}
        onClose={() => setBillingMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setBillingMessage(null)}
          sx={{ width: "100%" }}
        >
          {billingMessage}
        </Alert>
      </Snackbar>
      <Fade in={true} timeout={750}>
        <Box sx={outerSx}>
          <Box sx={innerSliderSx}>
            <Slider key={carouselSlickKey || "empty"} {...slickSettings}>
              {slides.map((slide, i) => (
                <AdSlide
                  key={slide.__slickDup ? `dup-${i}` : `slide-${i}`}
                  image={slide.logo}
                  buttonText={slide.button}
                  buttonClick={slide.buttonClick}
                  message={slide.message}
                  coordinates={slide.coordinates}
                  facilityType={slide.facilityType}
                  setFilter={setFilter}
                  website={slide.website}
                  isPaidAd={slide.isPaidAd}
                  isBuiltIn={slide.isBuiltIn}
                  title={slide.title}
                  mapCarouselLinkToVideo={slide.mapCarouselLinkToVideo}
                  mapCarouselYoutubeVideoId={slide.mapCarouselYoutubeVideoId}
                  mapCarouselLearnMoreHref={slide.mapCarouselLearnMoreHref}
                  onWatchVideo={() => {
                    if (slide.mapCarouselYoutubeVideoId) {
                      setYoutubeModalVideoId(slide.mapCarouselYoutubeVideoId);
                    }
                  }}
                  onBeforeOpenPaidWebsite={
                    slide.website
                      ? async () => {
                          if (
                            slide.type === LISTING_TYPE_LOCAL_CAROUSEL &&
                            slide.mapCarouselCpcChargeCents > 0
                          ) {
                            const d = await debitMapCarouselCpcClickIfNeeded({
                              zip: slide.mapCarouselSourceZip,
                              listingDocId: slide.mapCarouselListingDocId,
                              slideId: slide.carouselSlideId,
                              chargeCents: slide.mapCarouselCpcChargeCents,
                            });
                            if (!d.ok) {
                              setBillingMessage(
                                d.message ||
                                  "Could not verify ad billing for this click."
                              );
                              throw new Error("mapCarouselBillingBlocked");
                            }
                          }
                          recordCarouselClickAndStashForInternalPath(
                            slide.website,
                            slide.carouselSlideId
                          );
                        }
                      : undefined
                  }
                />
              ))}
            </Slider>
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default AdCarousel;
